import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';
import { Stream } from '@cloudflare/stream-react';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ProtectedResponsiveImage,
} from '../../../components';

import RoomMockupUpload from './RoomMockupUpload';
import RoomMockupDisplay from './RoomMockupDisplay';
import { createMockupStorage } from '../../../services/MockupStorageService';
import { useDispatch, useSelector } from 'react-redux';
import { selectMockupUpload } from '../../../ducks/mockupUpload.duck';

// Copied directly from
// `node_modules/react-image-gallery/styles/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showThumbnails: false,
  infinite: false,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Object} [props.video] - The video data with videoId, thumbnailUrl, streamUrl
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @param {Object} [props.currentUser] - The current user
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEditingRoomMockup, setIsEditingRoomMockup] = useState(false);
  const [roomMockupData, setRoomMockupData] = useState(null);
  const galleryRef = useRef(null);
  const intl = useIntl();
  const dispatch = useDispatch();
  const { completedAt } = useSelector(selectMockupUpload);
  const { rootClassName, className, images, video, imageVariants, thumbnailVariants, currentUser, listing, onGalleryReady } = props;
  const thumbVariants = thumbnailVariants || imageVariants;

  // Check if user is authenticated
  const isAuthenticated = !!currentUser?.id;

  // Create storage service instance
  const mockupStorage = createMockupStorage({
    isAuthenticated,
    dispatch,
    currentUser,
  });

  // Load mockup data on mount and when upload completes - from appropriate storage
  // completedAt changes when MockupPollManager finishes processing (for anonymous users)
  // currentUser changes when authenticated user's mockup is saved to their profile
  useEffect(() => {
    mockupStorage.get().then(data => {
      if (data) {
        setRoomMockupData(data);
      }
    }).catch(err => {
      console.error('Failed to load mockup data:', err);
    });
  }, [currentUser, completedAt]);

  const hasRoomMockup = !!(roomMockupData?.processedImageUrl || roomMockupData?.originalImageUrl || roomMockupData?.imageUrl);

  const mockupReadyImageId = listing?.attributes?.publicData?.mockupReadyImageId;
  const showMockupFeatures = !!mockupReadyImageId;

  const handleUploadSuccess = (newRoomMockupData) => {
    setRoomMockupData(newRoomMockupData);
  };

  const handleDelete = () => {
    setRoomMockupData(null);
  };

  // Create combined media items
  const mediaItems = [];

  // Check if mockup should be shown first (default: true)
  const showMockupFirst = roomMockupData?.showMockupFirst ?? true;

  // Prepare mockup item if available
  let mockupItem = null;
  if (hasRoomMockup && showMockupFeatures) {
    // Prefer processed image, fallback to original or legacy imageUrl
    const displayImageUrl = roomMockupData.processedImageUrl || roomMockupData.originalImageUrl || roomMockupData.imageUrl;

    // Find the mockup-ready image by ID
    const mockupReadyImage = images.find(img => {
      const imgId = img?.id?.uuid || img?.id;
      return imgId === mockupReadyImageId;
    });

    // Fallback to first image if mockup-ready image not found
    const artworkImage = mockupReadyImage || images[0];

    // Get the artwork thumbnail URL using thumbnail variants (not high-quality variants)
    const artworkThumbnailUrl = artworkImage?.attributes?.variants?.[thumbVariants[0]]?.url;

    mockupItem = {
      type: 'roomMockupDisplay',
      original: '', // Required by react-image-gallery
      roomImageUrl: displayImageUrl,
      originalImageUrl: roomMockupData.originalImageUrl,
      processedImageUrl: roomMockupData.processedImageUrl,
      roomMockupData: roomMockupData, // Pass full data for artwork placement
      artworkImage: artworkImage,
      artworkVariants: imageVariants,
      listing: listing, // Pass listing for dimension data
      thumbnail: artworkThumbnailUrl, // Use artwork thumbnail variant
      alt: intl.formatMessage({ id: 'RoomMockupDisplay.altText' }),
      thumbAlt: intl.formatMessage({ id: 'RoomMockupDisplay.altText' }),
    };
  }

  // Add mockup FIRST if enabled
  if (mockupItem && showMockupFirst) {
    mediaItems.push(mockupItem);
  }

  // Add first image
  if (images.length > 0) {
    const img = images[0];
    mediaItems.push({
      type: 'image',
      original: '', // Required by react-image-gallery
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    });
  }

  // Add video as second item if available
  if (video?.videoId) {
    const thumbnailUrl = `https://videodelivery.net/${video.videoId}/thumbnails/thumbnail.jpg`;
    mediaItems.push({
      type: 'video',
      videoId: video.videoId,
      thumbnailUrl,
      original: '', // Required by react-image-gallery
      thumbnail: thumbnailUrl, // Use constructed thumbnail URL
      alt: intl.formatMessage({ id: 'ListingImageGallery.videoAltText' }),
      thumbAlt: intl.formatMessage({ id: 'ListingImageGallery.videoThumbnailAltText' }),
    });
  }

  // Add remaining images (starting from index 1)
  images.slice(1).forEach((img, i) => {
    mediaItems.push({
      type: 'image',
      original: '', // Required by react-image-gallery
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 2, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 2, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    });
  });

  // Add mockup at the VERY END if NOT showing first (before upload placeholder)
  if (mockupItem && !showMockupFirst) {
    mediaItems.push(mockupItem);
  }

  // Add room mockup upload placeholder as last item (for all users without a room and if mockup features are enabled)
  // No longer requires authentication - anonymous users can now upload mockups
  if (!hasRoomMockup && showMockupFeatures) {
    mediaItems.push({
      type: 'roomMockupUpload',
      original: '', // Required by react-image-gallery
      thumbnail: '/static/images/mockupIcon.png',
      alt: 'Mockup',
      thumbAlt: 'Mockup',
    });
  }

  // if more then 1 media item, show thumbnails
  IMAGE_GALLERY_OPTIONS.showThumbnails = mediaItems.length > 1;

  // Find the index of the mockup slides (if they exist)
  const mockupUploadIndex = mediaItems.findIndex(item => item.type === 'roomMockupUpload');
  const mockupDisplayIndex = mediaItems.findIndex(item => item.type === 'roomMockupDisplay');

  // Use refs to avoid stale closures in callbacks
  const indicesRef = useRef({ mockupUploadIndex, mockupDisplayIndex, currentIndex });
  indicesRef.current = { mockupUploadIndex, mockupDisplayIndex, currentIndex };

  // Expose gallery navigation methods to parent via callback - only once on mount
  useEffect(() => {
    if (onGalleryReady) {
      onGalleryReady({
        slideToIndex: (index) => {
          if (galleryRef.current && indicesRef.current.currentIndex !== index) {
            galleryRef.current.slideToIndex(index);
          }
        },
        slideToMockupUpload: () => {
          const { mockupUploadIndex: idx, currentIndex: curr } = indicesRef.current;
          if (idx >= 0 && galleryRef.current && curr !== idx) {
            galleryRef.current.slideToIndex(idx);
          }
        },
        slideToMockupDisplay: () => {
          const { mockupDisplayIndex: idx, currentIndex: curr } = indicesRef.current;
          if (idx >= 0 && galleryRef.current && curr !== idx) {
            galleryRef.current.slideToIndex(idx);
          }
        },
        slideToMockup: () => {
          const { mockupDisplayIndex: displayIdx, mockupUploadIndex: uploadIdx, currentIndex: curr } = indicesRef.current;
          // Navigate to mockup display if exists, otherwise to upload
          // But only if not already there
          if (displayIdx >= 0 && galleryRef.current && curr !== displayIdx) {
            galleryRef.current.slideToIndex(displayIdx);
          } else if (uploadIdx >= 0 && galleryRef.current && curr !== uploadIdx) {
            galleryRef.current.slideToIndex(uploadIdx);
          }
        },
        getMockupInfo: () => ({
          hasMockupUpload: indicesRef.current.mockupUploadIndex >= 0,
          hasMockupDisplay: indicesRef.current.mockupDisplayIndex >= 0,
          mockupUploadIndex: indicesRef.current.mockupUploadIndex,
          mockupDisplayIndex: indicesRef.current.mockupDisplayIndex,
        }),
      });
    }
  }, [onGalleryReady]);
  
  // imageVariants are scaled variants.
  // Use first image for aspect ratio, or default to 16:9 if video is first
  const firstImage = images?.[0];
  const { aspectWidth, aspectHeight } = video?.videoId && !firstImage 
    ? { aspectWidth: 16, aspectHeight: 9 }
    : getFirstImageAspectRatio(firstImage, imageVariants[0]);
  
  const items = mediaItems;
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    if (item.type === 'video') {
      return (
        <AspectRatioWrapper
          width={16}
          height={9}
          className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        >
          <div className={css.itemCentering}>
            <div className={css.videoContainer}>
              <Stream
                src={item.videoId}
                controls={true}
                loop={true}
                autoplay
                muted={true}
                width="100%"
                height="100%"
                responsive={false}
                className={css.videoStream}
              />
            </div>
          </div>
        </AspectRatioWrapper>
      );
    }

    if (item.type === 'roomMockupUpload') {
      // Find the mockup-ready image by ID for upload display
      const mockupReadyImage = images.find(img => {
        const imgId = img?.id?.uuid || img?.id;
        return imgId === mockupReadyImageId;
      });

      // Fallback to first image if mockup-ready image not found
      const artworkImage = mockupReadyImage || images[0];

      return (
        <AspectRatioWrapper
          width={aspectWidth || 1}
          height={aspectHeight || 1}
          className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        >
          <RoomMockupUpload
            currentUser={currentUser}
            onUploadSuccess={handleUploadSuccess}
            artworkImage={artworkImage}
            artworkVariants={imageVariants}
            listing={listing}
          />
        </AspectRatioWrapper>
      );
    }

    if (item.type === 'roomMockupDisplay') {
      return (
        <AspectRatioWrapper
          width={aspectWidth || 1}
          height={aspectHeight || 1}
          className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        >
          <RoomMockupDisplay
            roomImageUrl={item.roomImageUrl}
            originalImageUrl={item.originalImageUrl}
            processedImageUrl={item.processedImageUrl}
            roomMockupData={item.roomMockupData}
            artworkImage={item.artworkImage}
            artworkVariants={item.artworkVariants}
            listing={item.listing}
            currentUser={currentUser}
            onDelete={handleDelete}
            onEditModeChange={setIsEditingRoomMockup}
          />
        </AspectRatioWrapper>
      );
    }
    
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ProtectedResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    if (item.type === 'video') {
      return (
        <div className={css.videoThumbWrapper}>
          <img
            src={item.thumbnail}
            alt={item.thumbAlt}
            className={css.thumb}
          />
          <div className={css.videoPlayIcon}>▶</div>
        </div>
      );
    }

    if (item.type === 'roomMockupUpload') {
      return (
        <div className={css.roomMockupThumbWrapper}>
          <div className={css.roomMockupThumbPlaceholder}>
            <div className={css.roomMockupUploadContainer}>
            <img
              src={item.thumbnail}
              alt={item.thumbAlt}
              className={css.roomMockupUploadIcon}
            />
          </div>
          </div>
        </div>
      );
    }

    if (item.type === 'roomMockupDisplay') {
      return (
        <div className={css.roomMockupThumbWrapper}>
          <div className={css.roomMockupThumbPlaceholder}>
            <div className={css.roomMockupArtworkContainer}>
              <img
                src={item.thumbnail}
                alt={item.thumbAlt}
                className={css.roomMockupArtworkThumb}
              />
              <img
                src="/static/images/mockupIcon.png"
                alt="Mockup"
                className={css.roomMockupIconOverlay}
              />
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div>
        <ProtectedResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    // Hide fullscreen button when on RoomMockupUpload slide
    const currentItem = items[currentIndex];
    if (currentItem?.type === 'roomMockupUpload') {
      return null;
    }

    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: mediaItems.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  const onSlide = (index) => {
    setCurrentIndex(index);
    // When sliding away from RoomMockupDisplay, disable edit mode
    const isRoomMockupDisplay = items[index]?.type === 'roomMockupDisplay';
    if (!isRoomMockupDisplay) {
      setIsEditingRoomMockup(false);
    }
  };

  // Check if current slide is RoomMockupDisplay and in edit mode
  const currentItem = items[currentIndex];
  const isEditModeActive = currentItem?.type === 'roomMockupDisplay' && isEditingRoomMockup;

  return (
    <ReactImageGallery
      ref={galleryRef}
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      onSlide={onSlide}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      disableSwipe={isEditModeActive}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
